import EmptyState from "@/components/EmptyState";
import Table, {
  TableFooter,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useQuery } from "@apollo/client";
import { Row } from "@tanstack/react-table";
import { useLocation, useNavigate } from "react-router-dom";
import { generatePath } from "react-router-dom";
import { Path } from "@/router";
import { toFormat } from "@/lib/dateHelpers";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { ProgramStatusTag } from "@/features/person/components/PersonObjectHeading";
import { capitalize } from "lodash";
import { renderEmptyCellState } from "@/components/Table/utils";
import { useState } from "react";
import PersonSideSheet from "@/features/person/components/PersonSideSheet/PersonSideSheet";
import {
  FACILITY_FORMER_EMPLOYEES_QUERY,
  type FacilityEmployment,
  type FacilityFormerEmployeesQueryResponse
} from "./queries";

export function FacilityFormerEmployeesTable({ facilityId }: { facilityId: string }) {
  const { data, loading } = useQuery<FacilityFormerEmployeesQueryResponse>(FACILITY_FORMER_EMPLOYEES_QUERY, {
    variables: { facilityId }
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);

  const employments = data?.facility.employments.nodes || [];

  function buildPersonPath(pgaId: string | null | undefined): Path | null {
    if (!pgaId) {
      return null;
    }
    const returnTo = location.pathname;
    return generatePath(`/people/:pgaId/person/overview?return_to=${encodeURIComponent(returnTo)}`, { pgaId }) as Path;
  }

  const columns = [
    ...buildAvatarDetailsColumn<FacilityEmployment>({
      isCompactLayout,
      getTitle: (row) => row.person.displayName,
      getSubtitle: (row) => row.person.latestProgram?.programType?.name || "",
      subtitleLabel: "Program",
      getPicture: (row) => row.person.profilePhoto,
      header: "Name",
      size: 230
    }),
    buildCopyTextColumn<FacilityEmployment>({
      accessorKey: "person.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.person.pgaId || "",
      shouldShowCopyButton: (row) => !!row.person.pgaId,
      enableSorting: false
    }),
    {
      accessorFn: (row: FacilityEmployment) => row.jobClassification,
      header: "Class on End Date",
      id: "jobClassification",
      enableSorting: false
    },
    {
      accessorFn: (row: FacilityEmployment) => row.formattedTitle || row.title,
      header: "Job Title",
      id: "title",
      enableSorting: false,
      size: 200,
      minSize: 200
    },
    {
      accessorFn: (row: FacilityEmployment) => row.startedOn,
      header: "Start Date",
      id: "startedOn",
      enableSorting: false,
      cell: ({ getValue }: { getValue: () => string | null }) => {
        const date = getValue();
        return renderEmptyCellState(date ? toFormat(date) : null);
      }
    },
    {
      accessorFn: (row: FacilityEmployment) => row.endedOn,
      header: "End Date",
      id: "endedOn",
      enableSorting: false,
      cell: ({ getValue }: { getValue: () => string }) => {
        const date = getValue();
        return renderEmptyCellState(date ? toFormat(date) : null);
      }
    },
    {
      id: "status",
      header: "Status",
      enableSorting: false,
      meta: {
        headerClassName: "hide-header-in-column"
      },
      cell: ({ row }: { row: Row<FacilityEmployment> }) => {
        const status = row.original.person?.latestProgram?.status;
        if (!status) {
          return null;
        }
        return (
          <ProgramStatusTag
            status={capitalize(status)}
            terminatedReason={row.original.person.latestProgram?.band?.terminatedReason}
            terminatesOn={row.original.person.latestProgram?.band?.terminatesOn}
          />
        );
      }
    },
    buildActionColumn<FacilityEmployment & { id: string }>({
      getActions: (row) => [
        {
          text: "View Person",
          section: "Actions",
          onClick: () => {
            const personPath = buildPersonPath(row.person.pgaId);
            if (personPath) {
              navigate(personPath);
            }
          }
        }
      ],
      onViewClick: (row) => {
        if (row.person.pgaId) {
          setSelectedPgaId(row.person.pgaId);
          setIsPersonSideSheetOpen(true);
        }
      },
      viewButtonTitle: "View person details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={employments}
        loading={loading}
        getRowLink={(row) => {
          const personPath = buildPersonPath(row.original.person.pgaId);
          return personPath ? { to: personPath } : null;
        }}
        columns={columns}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Former Employees"
            caption="There are no employees formerly associated to this facility."
            iconName="person"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={employments.length} />}
      />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
      />
    </>
  );
}
